class DevicePatchSelector extends HTMLElement {
  constructor() {
    super();

    this.selectedDevices = {};
    this.searchInput = null;
    this.optionsContainer = null;
    this.selectedTags = null;
    this.resultsEmpty = null;
    this.resultsGrid = null;
    this.checkboxes = [];
    this.contactMessage = null;
    this.notListedCheckbox = null;
    this.dropdownWrapper = null;
  }

  connectedCallback() {
    // Grab DOM refs inside this component
    this.searchInput = this.querySelector('#device-search');
    this.optionsContainer = this.querySelector('#dropdown-options');
    this.selectedTags = this.querySelector('#selected-tags');
    this.resultsEmpty = this.querySelector('#results-empty');
    this.resultsGrid = this.querySelector('#results-grid');
    this.contactMessage = this.querySelector('#contact-message');
    this.dropdownWrapper = this.querySelector('.device-dropdown-wrapper');

    if (
      !this.searchInput ||
      !this.optionsContainer ||
      !this.selectedTags ||
      !this.resultsEmpty ||
      !this.resultsGrid
    ) {
      return;
    }

    this.checkboxes = this.optionsContainer.querySelectorAll('input[type="checkbox"]');
    
    // Find the "Not listed" checkbox
    this.checkboxes.forEach((checkbox) => {
      if (this.isNotListed(checkbox.dataset.device)) {
        this.notListedCheckbox = checkbox;
      }
    });

    this.initEventListeners();
  }

  initEventListeners() {
    // Show / filter dropdown when input is focused or typed into
    this.searchInput.addEventListener('focus', () => {
      this.filterOptions(this.searchInput.value);
    });

    this.searchInput.addEventListener('input', () => {
      this.filterOptions(this.searchInput.value);
    });

    // Handle checkbox changes – just rebuild state each time
    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', (e) => {
        e.stopPropagation();
        if (checkbox.checked && !this.isNotListed(checkbox.dataset.device)) {
          this.clearNotListedSelection(false);
        }
        this.updateUI();
      });
    });

    // Close dropdown when clicking outside this component
    document.addEventListener('click', (e) => {
      const clickInsideWrapper =
        (this.dropdownWrapper && this.dropdownWrapper.contains(e.target)) || this.searchInput.contains(e.target);

      if (!clickInsideWrapper) {
        this.optionsContainer.classList.remove('show');
      }
    });
  }

  // Normalise text: lowercase and strip non-alphanumerics
  normalizeText(text) {
    return text.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // Show all options in all groups
  showAllOptions() {
    const groups = this.optionsContainer.querySelectorAll('.option-group');

    groups.forEach((group) => {
      group.style.display = 'block';
      const items = group.querySelectorAll('.option-item');
      items.forEach((item) => {
        item.style.display = 'flex';
      });
    });

    this.optionsContainer.classList.add('show');
    this.moveSpecialGroupsToEnd();
  }

  // Detect "Other" group by its label
  isOtherGroup(group) {
    const label = group.querySelector('.option-group-label');
    if (!label) return false;

    const normalized = this.normalizeText(label.textContent);
    return normalized === 'others' || normalized === 'other';
  }

  // Detect "Not listed" group by its label
  isNotListedGroup(group) {
    const label = group.querySelector('.option-group-label');
    if (!label) return false;

    const normalized = this.normalizeText(label.textContent);
    return normalized === 'notlisted';
  }

  // Check if a device is "Not listed"
  isNotListed(device) {
    const normalized = this.normalizeText(device);
    return normalized === 'notlisted';
  }

  // Uncheck the Not listed option and optionally refresh UI
  clearNotListedSelection(shouldUpdate = true) {
    if (this.notListedCheckbox && this.notListedCheckbox.checked) {
      this.notListedCheckbox.checked = false;
      if (shouldUpdate) {
        this.updateUI();
      }
      return true;
    }
    return false;
  }

  // Filter options when typing
  filterOptions(searchTerm) {
    const groups = Array.from(this.optionsContainer.querySelectorAll('.option-group'));

    if (!searchTerm || searchTerm.trim() === '') {
      this.showAllOptions();
      this.clearNotListedSelection();
      return;
    }

    this.optionsContainer.classList.add('show');

    const normalizedSearch = this.normalizeText(searchTerm);

    const otherGroups = [];
    const notListedGroups = [];
    const regularGroups = [];

    groups.forEach((group) => {
      if (this.isNotListedGroup(group)) {
        notListedGroups.push(group);
      } else if (this.isOtherGroup(group)) {
        otherGroups.push(group);
      } else {
        regularGroups.push(group);
      }
    });

    let hasAnyMatch = false;

    // Filter regular groups based on search
    regularGroups.forEach((group) => {
      let hasVisible = false;
      const items = group.querySelectorAll('.option-item');

      items.forEach((item) => {
        const text = item.querySelector('.option-text').textContent;
        const normalizedText = this.normalizeText(text);

        if (normalizedText.includes(normalizedSearch)) {
          item.style.display = 'flex';
          hasVisible = true;
          hasAnyMatch = true;
        } else {
          item.style.display = 'none';
        }
      });

      group.style.display = hasVisible ? 'block' : 'none';
    });

    // If no matches found in regular groups, show only "Not listed" option
    if (!hasAnyMatch) {
      // Hide all regular groups
      regularGroups.forEach((group) => {
        group.style.display = 'none';
      });

      // Hide Other groups
      otherGroups.forEach((group) => {
        group.style.display = 'none';
      });

      // Show only "Not listed" groups
      notListedGroups.forEach((group) => {
        group.style.display = 'block';
        const items = group.querySelectorAll('.option-item');
        items.forEach((item) => {
          item.style.display = 'flex';
        });
      });

      // Auto-check "Not listed" and show contact message
      if (this.notListedCheckbox && !this.notListedCheckbox.checked) {
        this.notListedCheckbox.checked = true;
        this.updateUI();
      }
    } else {
      // If matches found, show all items in Other and Not Listed groups
      otherGroups.forEach((group) => {
        group.style.display = 'block';
        const items = group.querySelectorAll('.option-item');
        items.forEach((item) => {
          item.style.display = 'flex';
        });
      });

      notListedGroups.forEach((group) => {
        group.style.display = 'block';
        const items = group.querySelectorAll('.option-item');
        items.forEach((item) => {
          item.style.display = 'flex';
        });
      });

      if (this.clearNotListedSelection(false)) {
        this.updateUI();
      }
    }

    this.moveSpecialGroupsToEnd();
  }

  // Ensure "Other" and "Not listed" groups are always at the bottom (Other first, then Not listed)
  moveSpecialGroupsToEnd() {
    const groups = Array.from(this.optionsContainer.querySelectorAll('.option-group'));
    const notListedGroups = groups.filter((group) => this.isNotListedGroup(group));
    const otherGroups = groups.filter((group) => this.isOtherGroup(group));
    const regularGroups = groups.filter((group) => !this.isOtherGroup(group) && !this.isNotListedGroup(group));

    // Order: regular groups, then Other groups, then Not listed groups
    [...regularGroups, ...otherGroups, ...notListedGroups].forEach((group) => {
      this.optionsContainer.appendChild(group);
    });
  }

  // Read which checkboxes are checked and return state
  getSelectedState() {
    const selectedDevices = {};
    const patchHandles = new Set();
    let hasNotListed = false;

    this.checkboxes.forEach((checkbox) => {
      if (!checkbox.checked) return;

      const value = checkbox.value;
      const device = checkbox.dataset.device;
      const patch = checkbox.dataset.patchHandle;

      selectedDevices[value] = { device, patch };
      patchHandles.add(patch);

      if (this.isNotListed(device)) {
        hasNotListed = true;
      }
    });

    return { selectedDevices, patchHandles, hasNotListed };
  }

  // Update tags + product cards
  updateUI() {
    const { selectedDevices, patchHandles, hasNotListed } = this.getSelectedState();
    this.selectedDevices = selectedDevices;

    // Update selected tags
    this.selectedTags.innerHTML = '';

    Object.entries(this.selectedDevices).forEach(([value, data]) => {
      if (this.isNotListed(data.device)) {
        return;
      }
      const tag = document.createElement('span');
      tag.className = 'device-tag';
      tag.innerHTML =
        data.device +
        '<button type="button" data-value="' +
        value +
        '" aria-label="Remove ' +
        data.device +
        '">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>' +
        '</svg>' +
        '</button>';

      this.selectedTags.appendChild(tag);
    });

    // Tag removal → uncheck the checkbox and refresh
    this.selectedTags.querySelectorAll('button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = btn.dataset.value;
        const checkbox = this.optionsContainer.querySelector('input[value="' + value + '"]');
        if (checkbox) {
          checkbox.checked = false;
        }
        this.updateUI();
      });
    });

    // Show/hide contact message
    if (this.contactMessage) {
      if (hasNotListed) {
        this.contactMessage.style.display = 'flex';
      } else {
        this.contactMessage.style.display = 'none';
      }
    }

    // Update product cards based on selected patches
    this.updateResults(patchHandles);
  }

  // Show only cards that belong to selected patch handles
  updateResults(patchHandles) {
    const { hasNotListed } = this.getSelectedState();
    const patchResults = this.querySelector('#patch-results');

    // If "Not listed" is selected, hide the entire patch results section
    if (hasNotListed) {
      if (patchResults) {
        patchResults.style.display = 'none';
      }
      return;
    }

    // Show patch results section
    if (patchResults) {
      patchResults.style.display = 'block';
    }

    const cards = this.resultsGrid.querySelectorAll('.patch-card');

    if (!patchHandles || patchHandles.size === 0) {
      // No selection → show empty state, hide all cards
      this.resultsEmpty.style.display = 'flex';
      this.resultsGrid.style.display = 'none';
      cards.forEach((card) => {
        card.style.display = 'none';
      });
      return;
    }

    let anyVisible = false;

    cards.forEach((card) => {
      const type = card.dataset.patchType;

      if (patchHandles.has(type)) {
        card.style.display = 'flex';
        anyVisible = true;

        const compatibleSpan = card.querySelector('.compatible-devices');
        if (compatibleSpan) {
          const devicesForThisPatch = Object.values(this.selectedDevices)
            .filter((d) => d.patch === type)
            .map((d) => d.device);

          compatibleSpan.textContent = devicesForThisPatch.join(', ');
        }
      } else {
        card.style.display = 'none';
      }
    });

    if (anyVisible) {
      this.resultsEmpty.style.display = 'none';
      this.resultsGrid.style.display = 'grid';
    } else {
      this.resultsEmpty.style.display = 'flex';
      this.resultsGrid.style.display = 'none';
    }
  }
}

// Register the custom element
if (!customElements.get('device-patch-selector')) {
  customElements.define('device-patch-selector', DevicePatchSelector);
}
